function _slicedToArray(arr, i) {
  return _arrayWithHoles(arr) || _iterableToArrayLimit(arr, i) || _unsupportedIterableToArray(arr, i) || _nonIterableRest();
}
function _nonIterableRest() {
  throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}
function _iterableToArrayLimit(arr, i) {
  if (typeof Symbol === "undefined" || !(Symbol.iterator in Object(arr))) return;
  var _arr = [];
  var _n = true;
  var _d = false;
  var _e = undefined;
  try {
    for (var _i = arr[Symbol.iterator](), _s; !(_n = (_s = _i.next()).done); _n = true) {
      _arr.push(_s.value);
      if (i && _arr.length === i) break;
    }
  } catch (err) {
    _d = true;
    _e = err;
  } finally {
    try {
      if (!_n && _i["return"] != null) _i["return"]();
    } finally {
      if (_d) throw _e;
    }
  }
  return _arr;
}
function _arrayWithHoles(arr) {
  if (Array.isArray(arr)) return arr;
}
function ownKeys(object, enumerableOnly) {
  var keys = Object.keys(object);
  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);
    if (enumerableOnly) symbols = symbols.filter(function (sym) {
      return Object.getOwnPropertyDescriptor(object, sym).enumerable;
    });
    keys.push.apply(keys, symbols);
  }
  return keys;
}
function _objectSpread(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = arguments[i] != null ? arguments[i] : {};
    if (i % 2) {
      ownKeys(Object(source), true).forEach(function (key) {
        _defineProperty(target, key, source[key]);
      });
    } else if (Object.getOwnPropertyDescriptors) {
      Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
      ownKeys(Object(source)).forEach(function (key) {
        Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
      });
    }
  }
  return target;
}
function _defineProperty(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }
  return obj;
}
function _toConsumableArray(arr) {
  return _arrayWithoutHoles(arr) || _iterableToArray(arr) || _unsupportedIterableToArray(arr) || _nonIterableSpread();
}
function _nonIterableSpread() {
  throw new TypeError("Invalid attempt to spread non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}
function _unsupportedIterableToArray(o, minLen) {
  if (!o) return;
  if (typeof o === "string") return _arrayLikeToArray(o, minLen);
  var n = Object.prototype.toString.call(o).slice(8, -1);
  if (n === "Object" && o.constructor) n = o.constructor.name;
  if (n === "Map" || n === "Set") return Array.from(o);
  if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen);
}
function _iterableToArray(iter) {
  if (typeof Symbol !== "undefined" && Symbol.iterator in Object(iter)) return Array.from(iter);
}
function _arrayWithoutHoles(arr) {
  if (Array.isArray(arr)) return _arrayLikeToArray(arr);
}
function _arrayLikeToArray(arr, len) {
  if (len == null || len > arr.length) len = arr.length;
  for (var i = 0, arr2 = new Array(len); i < len; i++) {
    arr2[i] = arr[i];
  }
  return arr2;
}
import React from 'react';
import { useLazyRef } from '@shopify/react-hooks';
import { I18n } from "./i18n.mjs";
import { I18nContext, I18nIdsContext, I18nParentContext } from "./context.mjs";
export function useI18n(options) {
  var manager = React.useContext(I18nContext);
  if (manager == null) {
    throw new Error('Missing i18n manager. Make sure to use an <I18nContext.Provider /> somewhere in your React tree.');
  }
  var registerOptions = React.useRef(options);
  if (shouldRegister(registerOptions.current) !== shouldRegister(options)) {
    throw new Error('You switched between providing registration options and not providing them, which is not supported.');
  } // Yes, this would usually be dangerous. But just above this line, we check to make
  // sure that they never switch from the case where `options == null` to `options != null`,
  // so we know that a given use of this hook will only ever hit one of these two cases.

  /* eslint-disable react-hooks/rules-of-hooks */

  if (options == null) {
    return useSimpleI18n(manager);
  } else {
    return useComplexI18n(options, manager);
  }
  /* eslint-enable react-hooks/rules-of-hooks */
}
function useComplexI18n(_ref, manager) {
  var id = _ref.id,
    fallback = _ref.fallback,
    translations = _ref.translations;
  var managerRef = React.useRef(null);
  var unsubscribeRef = React.useRef(noop);
  var parentIds = React.useContext(I18nIdsContext); // Parent IDs can only change when a parent gets added/ removed,
  // which would cause the component using `useI18n` to unmount.
  // We also don't support the `id` changing between renders. For these
  // reasons, it's safe to just store the IDs once and never let them change.

  var ids = useLazyRef(function () {
    return id ? [id].concat(_toConsumableArray(parentIds)) : parentIds;
  }); // When the manager changes, we need to do the following IMMEDIATELY (i.e.,
  // not in a useEffect callback):
  //
  // 1. Register the component’s translations. This ensures that the first render gets
  //    the synchronous translations, if available.
  // 2. Unsubscribe from changes to a previous manager.
  // 3. Subscribe to changes from the new manager. This ensures that if the subscription
  //    is updated between render and `useEffect`, the state update is not lost.

  if (manager !== managerRef.current) {
    managerRef.current = manager;
    unsubscribeRef.current();
    unsubscribeRef.current = manager.subscribe(ids.current, function (_ref2, details) {
      var translations = _ref2.translations,
        loading = _ref2.loading;
      var newI18n = new I18n(translations, _objectSpread(_objectSpread({}, details), {}, {
        loading: loading
      }));
      i18nRef.current = newI18n;
      setI18n(newI18n);
    });
    if (id && (translations || fallback)) {
      manager.register({
        id: id,
        translations: translations,
        fallback: fallback
      });
    }
  }
  var _React$useState = React.useState(function () {
      var managerState = manager.state(ids.current);
      var translations = managerState.translations,
        loading = managerState.loading;
      return new I18n(translations, _objectSpread(_objectSpread({}, manager.details), {}, {
        loading: loading
      }));
    }),
    _React$useState2 = _slicedToArray(_React$useState, 2),
    i18n = _React$useState2[0],
    setI18n = _React$useState2[1];
  var i18nRef = React.useRef(i18n);
  React.useEffect(function () {
    return unsubscribeRef.current;
  }, []); // We use refs in this component so that it never changes. If this component
  // is regenerated, it will unmount the entire tree of the previous component,
  // which is usually not desirable. Technically, this does leave surface area
  // for a bug to sneak in: if the component that renders this does so inside
  // a component that blocks the update from passing down, nothing will force
  // this component to re-render, so no descendants will get the new ids/ i18n
  // value. Because we don't actually have any such cases, we're OK with this
  // for now.

  var shareTranslationsComponent = useLazyRef(function () {
    return function ShareTranslations(_ref3) {
      var children = _ref3.children;
      return /*#__PURE__*/React.createElement(I18nIdsContext.Provider, {
        value: ids.current
      }, /*#__PURE__*/React.createElement(I18nParentContext.Provider, {
        value: i18nRef.current
      }, children));
    };
  });
  return [i18n, shareTranslationsComponent.current];
}
function useSimpleI18n(manager) {
  var i18n = React.useContext(I18nParentContext) || new I18n([], manager.details);
  return [i18n, IdentityComponent];
}
function IdentityComponent(_ref4) {
  var children = _ref4.children;
  return /*#__PURE__*/React.createElement(React.Fragment, null, children);
}
function shouldRegister() {
  var _ref5 = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
    fallback = _ref5.fallback,
    translations = _ref5.translations;
  return fallback != null || translations != null;
}
function noop() {}